import React, {Component} from "react";
import {Button, CardBody} from "gd-react";
import GridDuck from "gridduck";
import GdModal from "../../components/GdModal/GdModal";
import "./dataFileImport.scss";

// Reads a File and resolves its raw base64 content (without the `data:...;base64,` prefix).
function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
            let result = reader.result || "";
            let comma = result.indexOf(",");
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.onerror = () => reject(reader.error || new Error("Couldn't read file"));
        reader.readAsDataURL(file);
    });
}

// Modal to upload/preview one or more CSV files against a data file import config.
class FileImportUploadModal extends Component {
    constructor(props) {
        super(props);
        this.state = {files: [], error: null, previewResult: null};
        ["onFilesSelected", "removeFile", "submit", "handlePreview", "handleImport", "closeModal"]
            .forEach((f) => (this[f] = this[f].bind(this)));
    }

    onFilesSelected(e) {
        let selected = Array.from(e.target.files || []);
        let existing = this.state.files;
        let merged = existing.concat(
            selected.filter((f) => !existing.find((x) => x.name === f.name && x.size === f.size))
        );
        this.setState({files: merged, previewResult: null, error: null});
        e.target.value = "";
    }

    removeFile(i) {
        let files = this.state.files.slice();
        files.splice(i, 1);
        this.setState({files: files, previewResult: null});
    }

    // id selects the mode the backend handler runs in: "preview" parses without saving, "upload" imports.
    submit(mode) {
        let self = this;
        this.setState({error: null});
        return Promise.all(this.state.files.map((f) => fileToBase64(f)))
            .then((files) => GridDuck.createDataFileImportUpload({
                action: mode,
                files: files,
                configId: this.props.item.id,
            }))
            .then((res) => {
                if (mode === "preview") {
                    self.setState({previewResult: res.previewResult});
                } else {
                    self.closeModal(res);
                }
                return res;
            })
            .catch((err) => {
                self.setState({error: (err && err.message) || "Upload failed"});
                return Promise.reject(err);
            });
    }

    handlePreview() {
        return this.submit("preview");
    }

    handleImport() {
        return this.submit("upload");
    }

    closeModal(res) {
        this.props.onClose(res);
    }

    // preview is the lambda's PreviewResponse[] (one entry per uploaded file). Flatten the
    // parsed datapoints across files into a single table of rows that would be imported.
    renderPreviewTable(preview) {
        let rows = [];
        (preview || []).forEach((fileResult) => {
            (fileResult.dataPoints || []).forEach((dp) => rows.push(dp));
        });
        if (!rows.length) {
            return <p className={"info-text"}>No rows were parsed from the selected file(s).</p>;
        }
        let shown = rows.slice(0, 100);
        return (
            <div className={"upload-preview-table-wrapper"}>
                <table className={"upload-preview-table"}>
                    <thead>
                        <tr>
                            <th>Device</th>
                            <th>Date</th>
                            <th>Data Type</th>
                            <th>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((dp, i) => (
                            <tr key={i}>
                                <td>{dp.assetCode || dp.assetId || "—"}</td>
                                <td>{dp.date ? new Date(dp.date).toLocaleString() : ""}</td>
                                <td>{dp.dataType}</td>
                                <td>{dp.value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length > shown.length ?
                    <p className={"info-text"}>Showing first {shown.length} of {rows.length} rows.</p> : null}
            </div>
        );
    }

    render() {
        let files = this.state.files;
        let preview = this.state.previewResult;
        let footer = (
            <div className={"row"} style={{gap: "10px"}}>
                <Button label={"Preview"} outline color={"gd-grey"} progressRes disabled={!files.length} onClick={this.handlePreview}/>
                <Button label={"Import"} color={"gd-blue"} progressRes disabled={!files.length} onClick={this.handleImport}/>
            </div>
        );
        return (
            <GdModal
                title={this.props.item ? "Upload Files — " + this.props.item.name : "Upload Files"}
                open={this.props.open}
                footer={footer}
                onClose={this.closeModal}>
                <CardBody>
                    <div className={"data-file-import"}>
                        <p className={"info-text"}>Select one or more CSV files to import using this configuration.</p>
                        <div className={"row"}>
                            <input type={"file"} accept={".csv,text/csv"} multiple onChange={this.onFilesSelected}/>
                        </div>
                        {files.length ? (
                            <div className={"upload-file-list"}>
                                {files.map((f, i) => (
                                    <div className={"upload-file-row"} key={f.name + i}>
                                        <span className={"upload-file-name"}>{f.name}</span>
                                        <span className={"upload-file-remove"} onClick={() => this.removeFile(i)}>✕</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {this.state.error ? <p className={"error-text"}>{this.state.error}</p> : null}
                        {preview ? (
                            <div className={"upload-preview"}>
                                <p className={"sub-heading"}>Preview</p>
                                {this.renderPreviewTable(preview)}
                            </div>
                        ) : null}
                    </div>
                </CardBody>
            </GdModal>
        );
    }
}

export default FileImportUploadModal;
